import { TextWidgetEditor, useGuardEditorState, type VariableMap } from '@features/rich-text-editor';
import {
  GridDashboardTextItem,
  InsightDashboardItemType,
  TextSubType,
  DisplayMode,
} from '@g17eco/types/insight-custom-dashboard';
import classNames from 'classnames';
import { MetricWidgetDisplay } from './MetricWidgetDisplay';
import './styles.scss';

const commonClassnames = 'w-100 h-100 d-flex align-items-start text-break overflow-auto';

type Props = {
  item: Pick<GridDashboardTextItem, 'type' | 'subType' | 'text' | 'editorState' | 'displayMode' | 'title'>;
  isEditing?: boolean;
  variableMap: VariableMap;
  effectiveDate?: string;
  period?: string;
  onViewSource?: () => void;
  questionReference?: React.ReactNode;
};

export const TextWidget = ({
  item,
  isEditing,
  variableMap,
  effectiveDate,
  period,
  onViewSource,
  questionReference,
}: Props) => {
  const { type, subType, text, editorState, displayMode, title } = item;

  if (subType === TextSubType.Metric) {
    if (displayMode === DisplayMode.Widget) {
      return (
        <MetricWidgetDisplay
          text={text}
          effectiveDate={effectiveDate}
          period={period}
          title={title}
          onViewSource={onViewSource}
        />
      );
    }
    return (
      <MetricTextArea
        text={text}
        isEditing={isEditing}
        questionReference={questionReference}
      />
    );
  }

  if (editorState) {
    return <RichText editorState={editorState} isEditing={isEditing} variableMap={variableMap} />;
  }

  switch (type) {
    case InsightDashboardItemType.Headline:
      return <Headline text={text} isEditing={isEditing} />;
    case InsightDashboardItemType.Text:
      return <TextArea text={text} isEditing={isEditing} />;
    default:
      return null;
  }
};

const TextArea = ({ text, isEditing }: Pick<Props, 'isEditing'> & Pick<GridDashboardTextItem, 'text'>) => {
  return (
    <div
      className={classNames(
        { 'background-ThemeBgExtralight': isEditing },
        commonClassnames,
        'text-ThemeTextMedium text-area-widget',
      )}
    >
      {text}
    </div>
  );
};

const MetricTextArea = ({
  text,
  isEditing,
  questionReference,
}: Pick<Props, 'isEditing' | 'questionReference'> & Pick<GridDashboardTextItem, 'text'>) => {
  if (!text && questionReference) {
    return (
      <div
        className={classNames(
          { 'background-ThemeBgExtralight': isEditing },
          commonClassnames,
          'text-ThemeTextMedium text-area-widget',
        )}
      >
        <span className='text-muted d-inline text-nowrap'>No answer provided for&nbsp;</span>
        {questionReference}
      </div>
    );
  }

  return <TextArea text={text} isEditing={isEditing} />;
};

/** @deprecated */
const Headline = ({ text, isEditing }: Pick<Props, 'isEditing'> & Pick<GridDashboardTextItem, 'text'>) => {
  return (
    <h2
      className={classNames(
        { 'background-ThemeBgExtralight': isEditing },
        commonClassnames,
        'text-ThemeHeadingLight px-3 m-0'
      )}
    >
      {text}
    </h2>
  );
};

const RichText = ({
  editorState,
  isEditing,
  variableMap,
}: Pick<Props, 'isEditing' | 'variableMap'> & Pick<GridDashboardTextItem, 'editorState'>) => {
  const { guardEditorState } = useGuardEditorState(editorState);

  // Create a stable key that includes both editorState and variableMap
  // This forces remount when either changes, ensuring variables are replaced correctly
  const editorKey = JSON.stringify(guardEditorState.toJSON());
  const variablesKey = JSON.stringify(variableMap);
  const compositeKey = `${editorKey}-${variablesKey}`;

  return (
    <div className={classNames({ 'background-ThemeBgExtralight': isEditing }, 'w-100 h-100 overflow-auto text-editor-widget')}>
      <TextWidgetEditor
        editorState={guardEditorState}
        variableMap={variableMap}
        key={compositeKey}
      />
    </div>
  );
};
